import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { ActiveDeployment } from '../api';
import { formatClock, formatDuration, useNow } from './runTiming';
import { Icon } from './icons';
import { subscribeEvents } from '../events';
import { invalidateDeployment } from '../liveRefresh';
import { replaceEqualDeep } from '../queryCache';

/**
 * DeploymentToasts (observation 3): one persistent toast per deployment,
 * on every page, from the moment it starts until a little after it ends.
 * Each says which component of which project and environment, the
 * repository and commit, the step it is on and how far it got — the
 * percentage is the server's, computed from the run's own steps (see
 * domain.ComputeDeploymentProgress), never a timer. Clicking opens the
 * deployment. Live through the events socket; polling is the fallback.
 */

const FAST_POLL = 3000;
const SLOW_POLL = 20000;
const SUCCESS_LINGER = 15000;
const HIDDEN_KEY = 'commerce-ops-hidden-deploy-toasts';

function readHidden(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(HIDDEN_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function writeHidden(ids: Set<string>) {
  try {
    sessionStorage.setItem(HIDDEN_KEY, JSON.stringify([...ids].slice(-50)));
  } catch {
    // Storage blocked: hiding lasts for this page only.
  }
}

/** The organization's active deployments, kept fresh by events and polling. */
export function useActiveDeployments(): ActiveDeployment[] {
  const [items, setItems] = useState<ActiveDeployment[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  const anyRunning = useRef(false);
  // Status per deployment as of the previous read; null until the first read,
  // which only establishes the baseline (pages load their own lists then).
  const lastSeen = useRef<Map<string, string> | null>(null);

  const load = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    try {
      const res = await api.listActiveDeployments();
      if (!alive.current) return;
      // Only well-formed entries: a deployment without an id or progress is
      // not something a toast can describe.
      const valid = (res.deployments ?? []).filter((d) => d && typeof d.deployment_id === 'string' && d.progress && typeof d.finished === 'boolean');
      // Unchanged answers keep their identity: a poll that found nothing new
      // re-renders nothing.
      setItems((previous) => replaceEqualDeep(previous, valid));
      // A deployment that appeared or changed status under an open page:
      // refresh what it touched (its project's lists and statuses) in place.
      const seen = new Map(valid.map((d) => [d.deployment_id, `${d.status}:${d.finished}`]));
      const before = lastSeen.current;
      lastSeen.current = seen;
      if (before) {
        for (const d of valid) {
          if (before.get(d.deployment_id) !== seen.get(d.deployment_id)) invalidateDeployment(d.project_id, d.environment_id);
        }
      }
      anyRunning.current = valid.some((d) => !d.finished);
    } catch {
      // Keep what is shown; try again on the next tick.
    }
    if (!alive.current) return;
    const delay = anyRunning.current && document.visibilityState === 'visible' ? FAST_POLL : SLOW_POLL;
    timer.current = setTimeout(load, delay);
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeEvents((data) => {
      if (data.type === 'events.reconnected' || data.type.startsWith('execution.') || data.type.startsWith('deployment')) {
        if (debounce) clearTimeout(debounce);
        debounce = setTimeout(load, 300);
      }
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      if (debounce) clearTimeout(debounce);
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  return items;
}

function repoName(url?: string): string | null {
  if (!url) return null;
  const m = url.replace(/\.git$/, '').match(/[:/]([^/:]+\/[^/]+)$/);
  return m?.[1] ?? null;
}

type Outcome = 'running' | 'live' | 'partial' | 'failed' | 'other';

function outcomeOf(d: ActiveDeployment): Outcome {
  if (!d.finished) return 'running';
  switch (d.status) {
    case 'completed':
      return 'live';
    case 'completed_with_failures':
      return 'partial';
    case 'failed':
    case 'rolled_back':
      return 'failed';
    default:
      return 'other';
  }
}

function titleOf(d: ActiveDeployment, outcome: Outcome): string {
  const name = d.component_name || 'Deployment';
  if (d.rehearsal) return outcome === 'running' ? `Rehearsing ${name}` : `${name} rehearsal finished`;
  switch (outcome) {
    case 'running':
      return `Deploying ${name}`;
    case 'live':
      return `${name} deployment succeeded`;
    case 'partial':
      return `${name} deployment partially succeeded, some checks failed`;
    case 'failed':
      return d.status === 'rolled_back' ? `${name} was rolled back` : `${name} deployment failed`;
    default:
      return `${name} deployment ended`;
  }
}

export function DeploymentToasts() {
  const items = useActiveDeployments();
  const [hidden, setHidden] = useState<Set<string>>(() => readHidden());
  // Deployments seen running on this page: their outcome is shown when they
  // finish. A deployment that finished before the page opened is shown only
  // if it failed (a success nobody watched needs no toast).
  const seenRunning = useRef<Set<string>>(new Set());
  const [finishedAt, setFinishedAt] = useState<Record<string, number>>({});
  const [paused, setPaused] = useState(false);
  const running = items.some((d) => !d.finished);
  const now = useNow(running || Object.keys(finishedAt).length > 0);

  useEffect(() => {
    const updates: Record<string, number> = {};
    for (const d of items) {
      if (!d.finished) seenRunning.current.add(d.deployment_id);
      else if (finishedAt[d.deployment_id] === undefined) updates[d.deployment_id] = Date.now();
    }
    if (Object.keys(updates).length > 0) setFinishedAt((current) => ({ ...current, ...updates }));
  }, [items, finishedAt]);

  const hide = (id: string) =>
    setHidden((current) => {
      const next = new Set(current);
      next.add(id);
      writeHidden(next);
      return next;
    });

  const visible = useMemo(
    () =>
      items.filter((d) => {
        if (hidden.has(d.deployment_id)) return false;
        if (!d.finished) return true;
        const outcome = outcomeOf(d);
        const watched = seenRunning.current.has(d.deployment_id);
        if (outcome === 'live' || outcome === 'other') {
          // A success lingers briefly, then leaves on its own (not while
          // the pointer or focus is on the toasts).
          if (!watched) return false;
          const at = finishedAt[d.deployment_id];
          return paused || at === undefined || now - at < SUCCESS_LINGER;
        }
        return true;
      }),
    [items, hidden, finishedAt, now, paused],
  );

  if (visible.length === 0) return null;
  const shown = visible.slice(0, 4);
  return (
    <section
      className="deploy-toasts"
      aria-label="Deployments"
      data-testid="deploy-toasts"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* One status line, changed only when a deployment starts or ends: the
          progress ticking inside a toast is not announced. */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {shown.map((d) => titleOf(d, outcomeOf(d))).join('. ')}
      </p>
      <ol className="deploy-toasts__list">
        {shown.map((d) => (
          <DeploymentToast key={d.deployment_id} d={d} now={now} onHide={() => hide(d.deployment_id)} />
        ))}
      </ol>
      {visible.length > shown.length ? <p className="deploy-toasts__more">{visible.length - shown.length} more deployments running</p> : null}
    </section>
  );
}

function DeploymentToast({ d, now, onHide }: { d: ActiveDeployment; now: number; onHide: () => void }) {
  const outcome = outcomeOf(d);
  const repo = repoName(d.repository_url);
  const started = d.started_at ? Date.parse(d.started_at) : null;
  const ended = d.completed_at ? Date.parse(d.completed_at) : null;
  const elapsed = started !== null ? (ended ?? now) - started : null;
  const p = d.progress;
  const to = `/app/projects/${d.project_id}/deployments?env=${encodeURIComponent(d.environment_slug)}&deployment=${d.deployment_id}`;
  const title = titleOf(d, outcome);
  return (
    <li className={`deploy-toast deploy-toast--${outcome}`} data-testid={`deploy-toast-${d.deployment_id}`} data-outcome={outcome}>
      <Link className="deploy-toast__body" to={to} aria-label={`${title}. ${d.project_name}, ${d.environment_name}. Open the deployment`}>
        <span className="deploy-toast__head">
          <span className={`level-dot deploy-toast__dot deploy-toast__dot--${outcome}`} aria-hidden="true" />
          <strong className="deploy-toast__title">{title}</strong>
        </span>
        <span className="deploy-toast__where">
          {d.project_name}, {d.environment_name}
          {repo ? <span className="deploy-toast__repo"> in {repo}</span> : null}
        </span>
        <span className="deploy-toast__commit">
          <code translate="no">{d.commit_sha.slice(0, 7)}</code>
          {d.commit_message ? <span className="deploy-toast__message">{d.commit_message.split('\n')[0]}</span> : null}
        </span>
        {outcome === 'running' ? (
          <>
            <span className="deploy-toast__stage" data-testid="deploy-toast-stage">
              {p.current_stage_title && p.stage_total
                ? `Stage ${p.current_stage_index} of ${p.stage_total}: ${p.current_stage_title}`
                : p.current_step_title
                  ? `Step ${p.current_step_index} of ${p.total}: ${p.current_step_title}`
                  : `${p.done} of ${p.total} steps done`}
            </span>
            <span
              className="deploy-toast__bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={p.percent}
              aria-valuetext={`${p.percent}%, ${p.done} of ${p.total} steps done`}
            >
              <span className="deploy-toast__fill" style={{ transform: `scaleX(${p.percent / 100})` }} />
            </span>
            <span className="deploy-toast__meta">
              <span className="tabular-nums" data-testid="deploy-toast-percent">
                {p.percent}%
              </span>
              <span>
                {p.basis === 'previous_run'
                  ? 'estimated from the last successful run'
                  : p.current_stage_title && p.current_step_title
                    ? p.current_step_title
                    : `${p.done} of ${p.total} steps`}
              </span>
              {elapsed !== null ? <span className="tabular-nums">{formatClock(elapsed)}</span> : null}
            </span>
          </>
        ) : (
          <span className="deploy-toast__stage" data-testid="deploy-toast-stage">
            {outcome === 'failed' && p.current_stage_title && p.current_step_title
              ? `Stopped in ${p.current_stage_title}: ${p.current_step_title}`
              : outcome === 'failed' && p.current_step_title
              ? `Stopped at step ${p.current_step_index} of ${p.total}: ${p.current_step_title}`
              : elapsed !== null
                ? `Took ${formatDuration(elapsed)}`
                : null}
          </span>
        )}
      </Link>
      <button type="button" className="icon-button icon-button--ghost icon-button--sm deploy-toast__close" onClick={onHide} aria-label={`Hide ${title}`} title="Hide">
        <Icon name="close" size={16} />
      </button>
    </li>
  );
}
